import { useState } from 'react'
import { FileText, Upload } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { financeService, type FinanceContractRow } from '@/services/financeService'
import { resolvePublicAssetUrl } from '@/utils/resolvePublicAssetUrl'
import { FINANCE_DETAIL_FIELD_CLASS } from '@/components/Financeiro/FinanceDetailSectionCard'
import { toast } from 'sonner'

type Props = {
  projectId: string
  contracts: FinanceContractRow[]
  canUpload: boolean
  onUploaded: () => void
}

export function FinanceRequestContractSection({
  projectId,
  contracts,
  canUpload,
  onUploaded,
}: Props) {
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)

  const handleUpload = async () => {
    if (!file) return
    setUploading(true)
    try {
      await financeService.uploadContract(projectId, file)
      toast.success('Contrato assinado anexado ao projeto')
      setFile(null)
      onUploaded()
    } catch {
      toast.error('Falha ao enviar contrato')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className='space-y-4'>
      {contracts.length === 0 ? (
        <p className='rounded-lg border border-dashed border-white/[0.1] px-4 py-6 text-center text-sm text-text-muted'>
          Nenhum contrato assinado anexado ainda.
        </p>
      ) : (
        <ul className='space-y-2'>
          {contracts.map((c) => (
            <li
              key={c.id}
              className='flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.03] p-3'
            >
              <div
                className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg'
                style={{ backgroundColor: 'rgba(239, 68, 68, 0.12)', color: '#ef4444' }}
              >
                <FileText size={18} aria-hidden />
              </div>
              <div className='min-w-0 flex-1'>
                <p className='font-semibold text-text-primary'>Contrato · versão {c.version}</p>
                <p className='text-xs text-text-muted'>
                  {c.uploadedBy?.name ?? '—'} · {new Date(c.createdAt).toLocaleString('pt-BR')}
                </p>
              </div>
              <a
                href={resolvePublicAssetUrl(c.fileUrl)}
                target='_blank'
                rel='noreferrer'
                className='shrink-0 text-xs font-semibold text-accent hover:underline'
              >
                Abrir PDF
              </a>
            </li>
          ))}
        </ul>
      )}

      {canUpload && (
        <div className='rounded-xl border border-dashed border-white/[0.12] bg-white/[0.02] p-4'>
          <p className='mb-1 text-sm font-semibold text-text-primary'>Anexar contrato assinado</p>
          <p className='mb-3 text-xs text-text-muted'>
            Envie o PDF do contrato com assinatura do cliente para dar andamento ao projeto financeiro.
          </p>
          <input
            type='file'
            accept='application/pdf,.pdf'
            className={`${FINANCE_DETAIL_FIELD_CLASS} mb-3 file:mr-3 file:rounded file:border-0 file:bg-accent/15 file:px-3 file:py-1 file:text-xs file:font-semibold file:text-accent`}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          <Button type='button' size='sm' disabled={!file || uploading} onClick={() => void handleUpload()}>
            <Upload size={14} />
            {uploading ? 'Enviando…' : 'Enviar contrato assinado'}
          </Button>
        </div>
      )}
    </div>
  )
}
